import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Fab from '@mui/material/Fab';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';

// NEW: Import Supabase
import { supabase } from '../utils/supabaseClient.js'; 
import SEO from './SEO.js';
import box from '../assets/no.jpg'; // Using your generic image

const Search = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search).get('q');
  
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  const RESULTS_PER_PAGE = 12;

  // --- 1. NEW SEARCH FUNCTION ---
  const fetchSearchResults = async (pageNumber) => {
    try {
      if (!query) return;

      const from = pageNumber * RESULTS_PER_PAGE;
      const to = from + RESULTS_PER_PAGE - 1;

      // CALL SUPABASE (Using the 'fts' column we created in SQL)
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .textSearch('fts', query, { 
           type: 'websearch', // Allows user to use quotes like "blue motor"
           config: 'english'
        })
        .range(from, to);

      if (error) throw error;

      // Map Data (DB -> App format)
      const mappedData = data.map(p => ({
        ...p,
        name: p.title,
        imageUrls: p.image_urls || [],
        keywords: p.keywords || []
      }));

      // Update State
      if (pageNumber === 0) {
        setSearchResults(mappedData);
      } else {
        setSearchResults((prev) => [...prev, ...mappedData]);
      }

      if (data.length < RESULTS_PER_PAGE) {
        setHasMore(false);
      }
      setLoading(false);

    } catch (error) {
      console.error('Error fetching from Supabase:', error);
      setLoading(false);
      setHasMore(false);
    }
  };

  // Reset when query changes (e.g. user types new search)
  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setLoading(true);
    setSearchResults([]); 
    fetchSearchResults(0);
  }, [query]);

  // Load More logic
  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchSearchResults(nextPage);
  };

  // Scroll Button Logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) setShowScrollTop(true);
      else setShowScrollTop(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- COMPONENTS ---
  // Reusing your logic but simplifying for readability
  const ProductBox = ({ product }) => {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

    return (
      <div ref={ref} className={`col-6 col-md-4 col-lg-3 mb-4 animate-on-scroll ${inView ? 'is-visible' : ''}`}>
        <Link to={`/product/${product.id}`} className="product-link">
          <div className="card product-card">
            <div className="product-image-container">
              <img 
                src={product.imageUrls ? product.imageUrls[0] : box} 
                className="card-img-top product-image" 
                alt={product.name} 
                onError={(e) => {e.target.onerror = null; e.target.src = box;}} 
              />
            </div>
            <div className="card-body">
              <h5 className="card-title product-name">{product.name}</h5>
              <p className="card-text"><strong>Price: {product.price}</strong></p>
              <p className="card-text"><small className="text-muted">Category: {product.category}</small></p>
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const ProductSkeleton = () => (
    <div className="col-6 col-md-4 col-lg-3 mb-4">
      <div className="card product-card"><Skeleton height={200} /><div className="card-body"><Skeleton count={3} /></div></div>
    </div>
  );

  return (
    <div className="container mt-5">
      <SEO title={`Search Results for "${query}"`} />
      <h1 className="mb-4">Results for "{query}"</h1>
      
      <InfiniteScroll
        dataLength={searchResults.length}
        next={loadMore}
        hasMore={hasMore}
        loader={<div className="row">{[...Array(4)].map((_, i) => <ProductSkeleton key={i} />)}</div>}
      >
        <div className="row">
          {searchResults.map((product) => (
            <ProductBox key={product.id} product={product} />
          ))}
        </div>
      </InfiniteScroll>

      {searchResults.length === 0 && !loading && (
        <div className="text-center mt-5">
            <h3>No products found for "{query}"</h3>
            <p>Try checking your spelling or use a more general term.</p>
        </div>
      )}

      {showScrollTop && (
        <Fab color="primary" size="small" onClick={scrollToTop} style={{ position: 'fixed', bottom: 16, right: 16 }}>
          <KeyboardDoubleArrowUpIcon />
        </Fab>
      )}
    </div>
  );
};

export default Search;